<template>
  <section
    :class="{
      'pricing-comparison': true,
    }"
  >
    <div
      :class="{
        'container mx-auto grid p-0': true,
      }"
    >
      <h2
        :class="{
          'py-10 text-center text-28px font-black text-www-color-neutral-medium-dark': true,
        }"
      >
        {{ comparePlansHeader }}
      </h2>

      <div
        :class="{
          'grid lg:hidden': true,
        }"
        data-testid="pricingAccordionsContainer"
      >
        <template
          v-for="table in finale?.tabular"
          :key="table.heading"
        >
          <div
            :class="{
              grid: true,
            }"
          >
            <h3
              v-if="table.heading"
              :class="{
                'border border-b-0 border-www-color-neutral-light px-[15px] py-30px text-center text-xl font-extrabold uppercase tracking-wider text-www-color-primary-lighter': true,
              }"
            >
              {{ table.heading }}
            </h3>

            <ul>
              <template
                v-for="feat in table.rows"
                :key="feat.feature?._id"
              >
                <PricingComparisonAccordionListItem :label="feat.feature?.prefLabel || ''">
                  <p
                    :class="{
                      'text-13px text-www-color-neutral-medium': true,
                    }"
                  >
                    {{ feat.feature?.definition }}
                  </p>

                  <ul
                    :class="{
                      'mt-4 grid gap-4': true,
                    }"
                  >
                    <li
                      v-for="(cell, j) in feat.map"
                      :key="j"
                      :class="{
                        'flex items-center justify-between border-b border-www-color-neutral-light pb-1': true,
                      }"
                    >
                      <span
                        :class="{
                          'text-sm font-bold text-www-color-neutral-medium-dark': true,
                        }"
                      >
                        {{ cell?.name }}
                      </span>

                      <span
                        v-if="typeof cell?.value === 'string'"
                        :class="{
                          'text-13px text-www-color-neutral-medium-dark': true,
                        }"
                        >{{ cell.value }}</span
                      >
                      <PricingComparisonIconCheck v-else-if="cell?.value" />
                      <PricingComparisonIconRectangle v-else />
                    </li>
                  </ul>
                </PricingComparisonAccordionListItem>
              </template>
            </ul>
          </div>
        </template>
      </div>

      <div
        :class="{
          'hidden bg-www-color-white lg:grid': true,
        }"
      >
        <table
          :class="{
            relative: true,
          }"
        >
          <thead
            :class="{
              'sticky top-28 z-10 bg-www-color-white': true,
            }"
          >
            <tr
              :class="{
                [gridLayoutClasses]: true,
              }"
            >
              <th
                scope="col"
                :class="{
                  'flex border-x border-www-color-neutral-very-light p-[15px] first:border-l-0': true,
                }"
              >
                {{}}
              </th>

              <template
                v-for="item in finale?.headerData"
                :key="item?.id"
              >
                <th
                  scope="col"
                  :class="{
                    'border-x border-www-color-neutral-very-light p-[15px] last:border-r-0': true,
                  }"
                >
                  <div
                    :class="{
                      'grid gap-4 pb-12': true,
                    }"
                  >
                    <span
                      :class="{
                        'text-lg font-black text-www-color-neutral-medium-dark': true,
                      }"
                    >
                      {{ item?.name }}
                    </span>

                    <Button
                      v-if="item?.button"
                      :button-props="{
                        ...item.button,
                        style: 'primary-alt',
                      }"
                    />
                  </div>
                </th>
              </template>
            </tr>
          </thead>
          <template
            v-for="(set, index) in finale?.tabular"
            :key="set.heading"
          >
            <tbody>
              <tr v-if="set.heading">
                <td
                  :class="{
                    'px-[15px] py-30px text-xl font-extrabold uppercase tracking-[1px] text-www-color-primary-lighter': true,
                  }"
                >
                  {{ set.heading }}
                </td>
              </tr>

              <template
                v-for="row in set.rows"
                :key="row.feature?._id"
              >
                <tr
                  :class="{
                    [gridLayoutClasses]: true,
                    'bg-www-color-neutral-off-white': index === 1,
                    'bg-www-color-white even:bg-www-color-neutral-off-white': index !== 1,
                  }"
                >
                  <td
                    :class="{
                      'relative break-words border-x border-www-color-neutral-very-light p-[15px] font-bold leading-7 tracking-wider text-www-color-neutral-medium-dark first:border-l-0 last:border-r-0': true,
                    }"
                  >
                    {{ row.feature?.prefLabel }}
                    <PricingComparisonTooltip :content="row.feature?.definition || ''" />
                  </td>

                  <template
                    v-for="(cell, j) in row.map"
                    :key="j"
                  >
                    <td
                      :class="{
                        'grid place-content-center border-x border-www-color-neutral-very-light first:border-l-0 last:border-r-0': true,
                      }"
                    >
                      <span
                        v-if="typeof cell?.value === 'string'"
                        :class="{
                          'text-center text-13px leading-6 text-www-color-neutral-medium-dark': true,
                        }"
                        >{{ cell.value }}</span
                      >
                      <PricingComparisonIconCheck v-else-if="cell?.value" />
                      <PricingComparisonIconRectangle v-else />
                    </td>
                  </template>
                </tr>
              </template>
            </tbody>
          </template>
        </table>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import PricingComparisonAccordionListItem from './PricingComparisonAccordionListItem.vue';
import PricingComparisonIconCheck from './PricingComparisonIconCheck.vue';
import PricingComparisonIconRectangle from './PricingComparisonIconRectangle.vue';
import PricingComparisonTooltip from './PricingComparisonTooltip.vue';
import Button from '~/components/Atoms/Button/Button.vue';
import { PricingComparisonQuery } from '~/queries/components/Organisms/PricingCompaision.query';
import type { HighlightedPlans, PricingComparisonQueryResult } from '~/sanity/types';
import { useRefsStore } from '~/composables/refsStore';

interface ComparisonTableType extends HighlightedPlans {
  comparePlansHeader?: string;
  pageType: 'pricing' | 'education';
}

type ConceptVisibility = {
  viewIn?: string[] | null;
};

type ComparisonConcept = {
  _id?: string;
  prefLabel?: string;
  definition?: string | null;
  broader?: { _ref?: string }[] | null;
  viewIn?: string[] | null;
};

const props = defineProps<ComparisonTableType>();
const { plans } = toRefs(props);

const { getResolvedPlan } = useRefsStore();
const gridLayoutClasses = computed(() =>
  plans.value ? (gridClasses[plans.value.length] ?? 'grid-cols-auto') : 'grid-cols-auto'
);
const gridClasses: Record<string, string> = {
  '2': 'grid grid-cols-[224px_repeat(2,1fr)]',
  '3': 'grid grid-cols-[224px_repeat(3,1fr)]',
  '4': 'grid grid-cols-[224px_repeat(4,1fr)]',
};

const { data: featureData } = await useWwwSanityQueryNonNull<PricingComparisonQueryResult>(
  PricingComparisonQuery,
  {}
);

const resolvedPlans = await Promise.all(
  props.plans
    ?.filter((plan) => plan.selectPlan?.reference?._ref)
    .map((plan) => plan.selectPlan?.reference?._ref)
    .filter((ref): ref is string => !!ref)
    .map((ref) => getResolvedPlan(ref)) || []
);

const topConcepts = featureData.value?.topConcepts;
const concepts = featureData.value?.concepts;

const isConceptVisible = ({ viewIn }: ConceptVisibility): boolean => {
  if (!viewIn?.length) return true;

  return viewIn.includes(props.pageType);
};

const filteredTopConcepts = topConcepts?.filter((topConcept: ConceptVisibility) =>
  isConceptVisible(topConcept)
);

const castDataToTable = (raw: HighlightedPlans) => {
  if (!raw) return;

  const headerData = raw.plans?.map((plan, i) => {
    const selectPlan = resolvedPlans[i]?.value;
    if (!selectPlan) return;
    return {
      id: plan._key,
      button: selectPlan.CTAButton,
      name: selectPlan.name,
    };
  });

  const tabular = filteredTopConcepts
    ?.map((concept: ComparisonConcept) => {
      const childConcepts = concepts?.filter(
        (childConcept: ComparisonConcept) => concept?._id === childConcept?.broader?.at(0)?._ref
      );
      const visibleChildConcepts = childConcepts?.filter((childConcept: ComparisonConcept) =>
        isConceptVisible(childConcept)
      );
      const mappedTopLevelFeatures = childConcepts?.length ? visibleChildConcepts : [concept];

      const rows = mappedTopLevelFeatures?.map((feature: ComparisonConcept) => {
        const map = raw.plans?.map((plan, i) => {
          const selectedPlan = resolvedPlans[i]?.value;
          const featureInPlan = selectedPlan?.features.find((pf) => {
            if (!pf.feature) return;

            return pf.feature._id === feature?._id;
          });
          const isFeatureInPlan = !!featureInPlan;
          const isFeature = isFeatureInPlan && featureInPlan.featureType;
          const value = isFeature ? featureInPlan.featureType : isFeatureInPlan;

          return {
            value,
            name: selectedPlan?.name,
          };
        });

        return {
          map,
          feature,
        };
      });

      if (!rows?.length) return null;

      return {
        heading: childConcepts?.length ? concept?.prefLabel : '',
        id: concept?._id,
        rows,
      };
    })
    .filter((tableEntry): tableEntry is NonNullable<typeof tableEntry> => tableEntry !== null);

  return {
    tabular,
    headerData,
  };
};

const finale = castDataToTable(props);
</script>
